import { useCallback, useEffect, useImperativeHandle, useRef, forwardRef } from 'react';

/**
 * SignaturePad — pointer-drawn signature canvas for the driver audit flow.
 *
 * Plain canvas + Pointer Events (works for finger, stylus and mouse), scaled
 * for devicePixelRatio so the exported PNG is crisp. Navy ink on white,
 * per brand. Parent controls sizing via the wrapper; the canvas fills it.
 *
 * Exposes via ref:
 *   - isEmpty(): boolean
 *   - clear(): void
 *   - toDataURL(): string | null   (PNG data URL, null when empty)
 */

export interface SignaturePadHandle {
  isEmpty: () => boolean;
  clear: () => void;
  toDataURL: () => string | null;
}

interface SignaturePadProps {
  /** Called whenever the pad transitions between empty and drawn. */
  onDirtyChange?: (dirty: boolean) => void;
  className?: string;
}

/** Canvas can't read CSS vars — resolve the brand navy from globals.css. */
function inkColor(): string {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--brix-primary')
    .trim();
  return v || '#1F4E79';
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad({ onDirtyChange, className }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const dirtyRef = useRef(false);
    const lastRef = useRef<{ x: number; y: number } | null>(null);

    const setupCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.lineWidth = 2.25;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = inkColor();
    }, []);

    useEffect(() => {
      setupCanvas();
      // Redrawing on resize wipes strokes — acceptable: rotation mid-signature
      // is rare and the driver just signs again.
      const onResize = () => {
        setupCanvas();
        if (dirtyRef.current) {
          dirtyRef.current = false;
          onDirtyChange?.(false);
        }
      };
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }, [setupCanvas, onDirtyChange]);

    const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handleDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      lastRef.current = pointFromEvent(e);
    };

    const handleMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const last = lastRef.current;
      if (!ctx || !last) return;
      const pt = pointFromEvent(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      lastRef.current = pt;
      if (!dirtyRef.current) {
        dirtyRef.current = true;
        onDirtyChange?.(true);
      }
    };

    const handleUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
      // A tap without movement still counts as a mark (a dot).
      if (drawingRef.current && lastRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (ctx && !dirtyRef.current) {
          const { x, y } = lastRef.current;
          ctx.beginPath();
          ctx.arc(x, y, 1.2, 0, Math.PI * 2);
          ctx.fillStyle = inkColor();
          ctx.fill();
          dirtyRef.current = true;
          onDirtyChange?.(true);
        }
      }
      drawingRef.current = false;
      lastRef.current = null;
      e.currentTarget.releasePointerCapture(e.pointerId);
    };

    useImperativeHandle(ref, () => ({
      isEmpty: () => !dirtyRef.current,
      clear: () => {
        setupCanvas();
        dirtyRef.current = false;
        onDirtyChange?.(false);
      },
      toDataURL: () => {
        if (!dirtyRef.current) return null;
        return canvasRef.current?.toDataURL('image/png') ?? null;
      },
    }));

    return (
      <canvas
        ref={canvasRef}
        className={className}
        style={{ touchAction: 'none', width: '100%', height: '100%', display: 'block' }}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        aria-label="Signature pad — sign with your finger"
      />
    );
  },
);
